import { useMemo } from 'react';
import type { ReactNode } from 'react';
import { COMPANY_STATUS, ROLE_LABEL, TRIP_STATUS, dateTime } from '../format';
import type { Trip } from '../types';
import { Badge, Btn, Card, CardTitle, Empty, Loading, Notice, SecHead } from '../components/ui';
import { CompanyOnboarding, LeaveCompanyButton } from './Company';
import { TripStops, tripProgressBadge } from './TripForm';
import { usePortal } from '../App';

/** Chofer sin más rol, por secciones del riel: «Mi empresa» (su empresa o el
    alta/unión con código) y «Mis viajes» (los que la oficina le asignó).
    La operación diaria (ruta, visitas, aportes) sigue en la app. */
export function DriverView({ section }: { section: string }) {
  const { data, session, busy, refresh } = usePortal();
  const placeName = useMemo(() => {
    const map = new Map([...(data?.places ?? []), ...(data?.companyPlaces ?? [])].map(p => [p.id, p]));
    return (id: string) => map.get(id)?.name ?? 'Lugar no disponible';
  }, [data]);
  // Marca sutil en las paradas que tocan un lugar frecuente de la empresa
  // (companyFavoriteIds solo llega a miembros con empresa activa).
  const favorites = useMemo(() => new Set(data?.companyFavoriteIds ?? []), [data]);
  const companyBadge = useMemo(() => {
    if (!favorites.size) return undefined;
    const label = `Lugar de ${data?.company?.name ?? session.companyName ?? 'tu empresa'}`;
    return (placeId: string): ReactNode => (favorites.has(placeId) ? <Badge tone="info">{label}</Badge> : null);
  }, [favorites, data, session.companyName]);

  if (!data) return <Loading label="Leyendo tu espacio…" />;

  // Con empresa pendiente el snapshot manda `company` con su estado pero sin
  // datos privados; la sesión conserva id/nombre para mostrar la tarjeta igual.
  const companyId = data.company?.id ?? session.companyId;
  const companyName = data.company?.name ?? session.companyName;
  const status = data.company?.status ?? (companyId ? 'pending' : null);
  const myRole = data.company?.role ?? session.companyRole ?? 'driver';
  const st = status ? COMPANY_STATUS[status] : null;
  const offices = data.companyMembers.filter(m => m.companyRole === 'office');
  const lastOffice = myRole === 'office' && offices.length > 0 && offices.length <= 1;

  // El servidor ya filtra myTrips a asignados/en curso; la separación queda por
  // si el contrato crece — los cerrados se pliegan en «Viajes recientes».
  const openTrips = data.myTrips.filter(t => t.status === 'assigned' || t.status === 'active').sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  const closedTrips = data.myTrips.filter(t => t.status === 'done' || t.status === 'cancelled').sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));

  const refreshBtn = <Btn tone="secondary" small busy={busy} onClick={() => void refresh()}>Actualizar</Btn>;

  return <>
    {section === 'empresa' && <>
      <SecHead title={`Hola, ${session.displayName || 'operador'}`} desc="Tu empresa y tu rol." actions={refreshBtn} />
      <Notice tone="info">Tu espacio está en la app: ahí armas tu ruta, registras visitas y aportas al directorio. Aquí consultas tu empresa y tus viajes asignados.</Notice>
      {companyId
        ? <Card>
            <CardTitle>Tu empresa</CardTitle>
            <div>
              <div className="name lg">{companyName || 'Tu empresa'}</div>
              <div className="actions u-mt-sm">
                {st && <Badge tone={st.tone}>{st.label}</Badge>}
                <Badge tone={myRole === 'office' ? 'info' : 'muted'}>Tu rol: {ROLE_LABEL[myRole] ?? myRole}</Badge>
              </div>
            </div>
            {status === 'pending' && <Notice tone="warning">Tu empresa espera la autorización del administrador del directorio; sus lugares, notas y viajes privados empezarán a llegar cuando quede activa.</Notice>}
            {status === 'blocked' && <Notice tone="danger">Tu empresa está suspendida: sus datos privados no llegan hasta que el administrador la autorice de nuevo.</Notice>}
            <div className="actions"><LeaveCompanyButton companyName={companyName ?? 'tu empresa'} lastOffice={lastOffice} /></div>
          </Card>
        : <CompanyOnboarding />}
    </>}

    {section === 'viajes' && <>
      <SecHead title="Mis viajes" desc="Los que la oficina te asignó; acéptalos y márcalos en la app." actions={refreshBtn} />
      <Card>
        {!openTrips.length && <Empty icon="route" title="Sin viajes" message="Cuando la oficina te asigne un viaje aparecerá aquí (y como aviso en la app)." />}
        <div className="list">
          {openTrips.map(trip => <TripItem key={trip.id} trip={trip} placeName={placeName} badge={companyBadge} />)}
        </div>
      </Card>

      {closedTrips.length > 0 && <details className="card flat collapsible">
        <summary className="card-title">Viajes recientes · {closedTrips.length}</summary>
        <div className="list">
          {closedTrips.map(trip => <TripItem key={trip.id} trip={trip} placeName={placeName} badge={companyBadge} />)}
        </div>
      </details>}
    </>}
  </>;
}

function TripItem({ trip, placeName, badge }: { trip: Trip; placeName(id: string): string; badge?(placeId: string): ReactNode }) {
  // Estado inesperado del backend → etiqueta cruda en gris, nunca undefined.
  const ts = TRIP_STATUS[trip.status] ?? { label: trip.status, tone: 'muted' as const };
  // El progreso por parada lo infiere el servidor de sus propias visitas
  // (stop.progress); se suma a la marca de lugar frecuente si la hay.
  const progress = tripProgressBadge(trip.stops);
  const stopBadge = (placeId: string): ReactNode => {
    const fav = badge?.(placeId);
    const pr = progress(placeId);
    if (!fav && !pr) return null;
    return <>{fav}{fav && pr ? ' ' : null}{pr}</>;
  };
  return <div className="item">
    <div className="row between multiline">
      <div className="title">Viaje · {trip.stops.length} parada{trip.stops.length === 1 ? '' : 's'}</div>
      <Badge tone={ts.tone}>{ts.label}</Badge>
    </div>
    <TripStops stops={trip.stops} placeName={placeName} badge={stopBadge} />
    {trip.notes && <div className="small muted">Notas: {trip.notes}</div>}
    <div className="tiny muted">Asignado {dateTime(trip.createdAt)} por {trip.createdByName}</div>
  </div>;
}
